<script setup>
import { onMounted, ref, inject, computed, watch } from 'vue'
import useValidation from '@/composables/useValidation'
import useLogger from '@/composables/useLogger'
import useCopyText from '@/composables/useCopyText'
import { useSqlDatasource } from './sqlDatasourceComposable'
import { useStore } from '@/store'
import codemirror from '@/components/shared/codemirror'
import { Splitpanes, Pane } from 'splitpanes'
import 'splitpanes/dist/splitpanes.css'

// const CodeMirror = require('codemirror/lib/codemirror.js')
const logger = useLogger('SqlDatasourceCard')
const flash = inject('flash')
const emit = defineEmits(['on-save', 'on-close'])

const store = useStore()
const copyToClipboard = useCopyText()

/**
 * Composables
 */

const { vruleRequired } = useValidation()

/* isLoading, */
const { loadSqlDatasource, buildNewSqlDatasource, loadAvailableConnections } = useSqlDatasource()

/**
 * Props
 */
const props = defineProps({
  datasourceId: {
    type: [Number, String],
    required: false
  },
  dealerId: {
    type: [Number, String],
    required: true
  }
})

/**
 * Data
 */
const editorOptions = ref({
  mode: {
    name: 'text/x-plsql'
    // scriptTypes: [{ matches: /\/x-handlebars-template|\/x-mustache/i, mode: null }]
  },
  lineNumbers: true,
  lineWrapping: false,
  matchBrackets: true,
  smartIndent: true,
  autofocus: true,
  theme: 'solarized light',
  extraKeys: { 'Ctrl-Space': 'autocomplete' },
  // hintOptions: { hint: this.mergeTagHints },
  viewportMargin: Infinity
})

const form = ref(null) // initialize to null - will be populated by el ref="form"
const fileUploadInput = ref(null) // initialize to null - will be populated by el ref="fileUploadInput"

const sqlDatasource = ref({})
const queryResults = ref({})
const availableConnections = ref([])
const copyDealersIdList = ref([])

const isQueryRunning = ref(false)
const isLoadingDataCsv = ref(false)
const isLoadingDataPreview = ref(false)
const isSaving = ref(false)

/**
 * Computed
 */

const tableData = computed(() => {
  if ((queryResults.value.data || []).length === 0) {
    return []
  } else {
    return queryResults.value.data
  }
})

const tableColumns = computed(() => {
  if ((queryResults.value.metadata || { fields: [] })['fields'].length === 0) {
    return []
  } else {
    return queryResults.value.metadata.fields.map((field) => {
      return {
        text: field,
        value: field
      }
    })
  }
})

const dealerList = computed(() => {
  const l = store.state.DealerStore.dealers
  return l.map((d) => {
    return {
      text: d.name,
      value: d.dms_key
    }
  })
})

const tablePane = ref(0)

const isR4Datasource = computed(() => {
  return sqlDatasource.value.connectionName && ['dms'].includes(sqlDatasource.value.connectionName)
})

/**
 * Watchers
 */

// WIP
// watch tablepane height and set max-height
watch(tablePane, (newVal, oldVal) => {
  if (newVal) {
    setPaneHeight()
  }
})

/**
 * Methods
 */

// WIP
function setPaneHeight() {
  const paneHeight = tablePane.value.$el.clientHeight
  logger.trace('setPaneHeight paneHeight: ', paneHeight)
  tablePane.value.$el.style.maxHeight = paneHeight + 'px'
}

function copyDealerIds() {
  if (copyDealersIdList.value.length === 0) {
    flash({
      message: 'No dealers selected to copy ID',
      type: 'mini',
      showClose: true,
      duration: 2500
    })
  } else {
    const dealerIds = copyDealersIdList.value.map((d, idx) => {
      if (idx === copyDealersIdList.value.length - 1) {
        // don't add a comma on the last item
        return `${d.value}  /* ${d.text} */`
      } else {
        return `${d.value},  /* ${d.text} */`
      }
    })

    copyToClipboard(dealerIds.join('\n'))
  }
}

function openFile() {
  fileUploadInput.value.click()
}

function loadFile() {
  const file = fileUploadInput.value.files[0]
  if (!file) {
    return
  }
  const reader = new FileReader()
  reader.onload = (fileLoadedEvent) => {
    sqlDatasource.value.sql = fileLoadedEvent.target.result
  }
  reader.readAsText(file)
}

function validateAndSubmit() {
  if (form.value.validate()) {
    isSaving.value = true
    sqlDatasource.value.save()
      .then((updatedSqlDs) => {
        sqlDatasource.value = updatedSqlDs
        emit('on-save')
        flash({
          showClose: true,
          message: 'Datasource saved.',
          type: 'mini',
          duration: 2500
        })
      })
      .catch(error => {
        logger.error(error)
        flash({
          message: 'Error saving datasource. ' + error.message,
          type: 'mini',
          showClose: true,
          duration: 0
        })
      })
      .finally(() => {
        isSaving.value = false
      })
  }
}

function checkDirtyAndExit() {
  if (sqlDatasource.value.isDirty) {
    if (confirm('You have unsaved changes. Are you sure you want to leave?')) {
      emit('on-save')
    }
  } else {
    emit('on-save')
  }
}

function runQuery() {
  isQueryRunning.value = true
  isLoadingDataPreview.value = true
  sqlDatasource.value.runQuery()
    .then((result) => {
      queryResults.value = result
    })
    .catch(error => {
      logger.error(error)
      flash({
        message: 'Error executing query. ' + error.message,
        type: 'mini',
        showClose: true,
        duration: 0
      })
    })
    .finally(() => {
      isQueryRunning.value = false
      isLoadingDataPreview.value = false
    })
}

async function downloadCsv() {
  isQueryRunning.value = true
  isLoadingDataCsv.value = true
  try {
    const url = await sqlDatasource.value.getLinkForCsvDownload()
    const downloadLink = document.createElement('a')
    downloadLink.setAttribute('href', url)
    downloadLink.setAttribute('style', 'display:none')
    downloadLink.setAttribute('target', '_blank')
    document.body.append(downloadLink)

    setTimeout(function () {
      downloadLink.click()
      downloadLink.remove()
    }, null)
  } catch (error) {
    logger.error(error)
    flash({
      message: 'Error downloading CSV. ' + error.message,
      type: 'mini',
      showClose: true,
      duration: 0
    })
  } finally {
    isQueryRunning.value = false
    isLoadingDataCsv.value = false
  }
}

/**
 * Lifecycle
 */
onMounted(async () => {
  if (props.datasourceId) {
    sqlDatasource.value = await loadSqlDatasource(props.datasourceId)
  } else {
    sqlDatasource.value = await buildNewSqlDatasource(props.dealerId)
  }

  availableConnections.value = await loadAvailableConnections()
})
</script>

<template>
  <v-card flat tile max-height="100vh">
    <v-form ref="form" lazy-validation>
      <v-toolbar color="secondary" dark>
        <v-text-field v-model="sqlDatasource.name" label="Name" outlined density="compact" append-icon="mdi-pencil"
          :rules="vruleRequired" required hide-details="auto" dense class="pa-2"></v-text-field>
        <v-spacer />
        <v-btn :depressed="true" color="secondary" @click="openFile" :disabled="isQueryRunning">
          <v-icon left dark>mdi-folder-open</v-icon>
          Open File
        </v-btn>

        <input type="file" ref="fileUploadInput" hidden="true" @change="loadFile" accept=".sql" />

        <v-btn :depressed="true" color="secondary" @click="downloadCsv" :disabled="isQueryRunning"
          :loading="isLoadingDataCsv">
          <v-icon left dark>mdi-file-delimited</v-icon>
          Download CSV
        </v-btn>
        <v-btn :depressed="true" color="secondary" @click="runQuery" :disabled="isQueryRunning"
          :loading="isLoadingDataPreview">
          <v-icon left dark>mdi-binoculars</v-icon>
          Preview Data
        </v-btn>
        <v-btn :depressed="true" color="secondary" @click.prevent="validateAndSubmit" :disabled="isQueryRunning">
          <v-icon left dark>mdi-content-save</v-icon>
          Save
        </v-btn>
        <v-btn :depressed="true" color="secondary" @click="checkDirtyAndExit" :disabled="isQueryRunning">
          <v-icon left dark>mdi-close-circle</v-icon>Close
        </v-btn>
      </v-toolbar>
      <v-toolbar color="secondary" dark>
        <v-select v-model="sqlDatasource.connectionName" :items="availableConnections" item-text="display_name"
          item-value="name" label="Connection Name" outlined :rules="vruleRequired" required hide-details="auto" dense
          class="pa-2"></v-select>

        <v-autocomplete v-if="isR4Datasource" v-model="copyDealersIdList" :items="dealerList" multiple outlined
          :return-object="true" chips clearable dense hide-selected hide-details="auto" min-width="300"
          :label="copyDealersIdList.length === 0 ? 'Select Dealer(s) to copy ID\'s' : 'Copy with side button'"
          class="pa-2"></v-autocomplete>

        <v-btn v-if="isR4Datasource && copyDealersIdList.length > 0" :depressed="true" color="secondary"
          :disabled="isQueryRunning" :loading="isLoadingDataPreview" @click="copyDealerIds">
          <v-icon left dark>mdi-copy</v-icon>
          Copy Dealer IDs
        </v-btn>

      </v-toolbar>

      <splitpanes class="default-theme" horizontal>
        <pane min-size="20" max-size="70">
          <codemirror v-model="sqlDatasource.sql" :options="editorOptions"></codemirror>
        </pane>
        <pane ref="tablePane">
          <v-data-table :headers="tableColumns" :items="tableData" :items-per-page="5" class="elevation-0 overflow-y-auto"
            item-key="id" dense :loading="isLoadingDataPreview" loading-text="Loading... Please Wait"
            no-data-text="Data Preview - No Data Available"></v-data-table>
        </pane>
      </splitpanes>
      <v-overlay :value="isSaving">
        <div class="text-center">
          <h2 class="mb-6">Saving Datasource</h2>
          <v-progress-circular indeterminate size="72"></v-progress-circular>
          <h3>Please wait while the datasource is post-processing...</h3>
          <h4>This may take a minute if the query is long-running</h4>
        </div>
      </v-overlay>
    </v-form>
  </v-card>
</template>

<style scoped>
.splitpanes.default-theme .splitpanes__pane {
  background-color: #fff;
}

:deep(.v-application--wrap) {
  max-height: 100vh;
  overflow-y: hidden;
}

.splitpanes__pane {
  overflow-y: scroll;
}

.v-application--wrap {
  height: 100vh;
  overflow: hidden;
}

.v-form {
  height: 100vh;
}

:deep(.v-text-field) {
  max-width: 350px;
}
</style>
